import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { utcPeriodGenerator } from '@iotv/datamodel-core';
import { InsertBehavior, isNonNaNNumber, localTimeToUTC, utcToLocalTime } from '@iotv/iotv-v3-types';
import { Button, FormLabel, Stack, TextField } from '@mui/material';
import { useContext, useReducer } from 'react';
import { NavSections } from '../../../../../../components/navigation/NavSections';
import { AppMainViewContext } from '../../../../../../ContextProviders/AppContextProvider';
import { AppLocalStorage } from '../../../../../../data/LocalStorage/AppLocalStorage';
import { Timezone } from '../../common/Timezone';
import { EntityProgrammeContext } from '../Reducers/ProgrammeReducer';
const debug = process.env.REACT_APP_DEBUG && false;
const reducer = (prev, action) => {
    const update = { ...prev };
    const { durationMins, totalDurationHours, localHr, localMin, utcMinsOffset } = action;
    if (utcMinsOffset) {
        update.utcMinsOffset = utcMinsOffset;
    }
    if (isNonNaNNumber(durationMins) && durationMins > 0) {
        update.durationMins = Math.floor(durationMins);
    }
    if (isNonNaNNumber(totalDurationHours)) {
        update.totalDurationHours = totalDurationHours;
    }
    if (isNonNaNNumber(localHr)) {
        const { utcHour, utcMin } = localTimeToUTC(localHr, update.localMin, update.utcMinsOffset);
        update.utcHour = utcHour;
        update.utcMin = utcMin;
        const local = utcToLocalTime(utcHour, utcMin, update.utcMinsOffset);
        update.localHr = local.localHr;
        update.localMin = local.localMin;
    }
    if (isNonNaNNumber(localMin)) {
        update.localMin = localMin;
        const { utcHour, utcMin } = localTimeToUTC(update.localHr, localMin, update.utcMinsOffset);
        const local = utcToLocalTime(utcHour, utcMin, update.utcMinsOffset);
        update.localHr = local.localHr;
        update.localMin = local.localMin;
    }
    AppLocalStorage.set(NavSections.THINGVIEW, 'entityProgrammeSlotCreation', update);
    return update;
};
export const CreateSlots = ({ close }) => {
    const getDefaultState = (utcMinsOffset) => {
        const defaultState = AppLocalStorage.get(NavSections.THINGVIEW, 'entityProgrammeSlotCreation') ?? {
            durationMins: 5,
            totalDurationHours: 3,
            utcHour: 6,
            utcMin: 0,
            localHr: 0,
            localMin: 0,
            utcMinsOffset: 0,
        };
        const { utcHour, utcMin } = localTimeToUTC(defaultState.localHr, defaultState.localMin, utcMinsOffset ?? 0);
        Object.assign(defaultState, { utcHour, utcMin });
        return defaultState;
    };
    const [{ ipData }] = useContext(AppMainViewContext);
    const { utcMinsOffset } = ipData ?? {};
    const defaultState = getDefaultState(utcMinsOffset);
    const [state, dispatch] = useContext(EntityProgrammeContext);
    const [localState, localDispatch] = useReducer(reducer, defaultState);
    const { durationMins, totalDurationHours, utcHour, utcMin, localHr, localMin } = localState;
    const { scheduler } = state;
    debug &&
        console.log(`Create Slots has`, {
            durationMins,
            totalDurationHours,
            utcHour,
            utcMin,
            localHr,
            localMin,
        });
    const handleCreateSlots = () => {
        if (scheduler) {
            if (scheduler.getSlots().length === 0) {
                const utcHourMax = (utcHour + totalDurationHours); /* this generates utc_start_hours above 23, so needs to be mutated before insert */
                const utcMinMax = utcMin;
                debug &&
                    console.log(`Create Slots creating generator with`, {
                        start: { utcHour, utcMin },
                        duration_mins: durationMins,
                        max: { utcHour: utcHourMax, utcMin: utcMinMax },
                    });
                const generator = utcPeriodGenerator({
                    start: { utcHour, utcMin },
                    duration_mins: durationMins,
                    max: { utcHour: utcHourMax, utcMin: utcMinMax },
                });
                generator.forEach((utcPeriod) => {
                    const { utc_start_hour } = utcPeriod;
                    if (utc_start_hour > 23) {
                        utcPeriod.utc_start_hour = utc_start_hour - 24;
                    }
                    scheduler.insertTimeslot(utcPeriod, InsertBehavior.SHIFT);
                });
                const action = {
                    type: 'Recalculate',
                    value: null,
                };
                dispatch(action);
            }
            else {
                debug && console.log(`WARN: scheduler has slots already`);
            }
        }
        close();
    };
    return (_jsxs(Stack, { gap: 1, marginBlockStart: 1, width: 'min(70cqw, 30em)', children: [_jsxs(Stack, { direction: 'row', justifyContent: 'space-between', children: [_jsx(FormLabel, { children: "Start" }), _jsx(Timezone, {})] }), _jsxs(Stack, { direction: 'row', gap: 1, children: [_jsx(TextField, { value: localHr,
                        label: 'Hour',
                        type: 'number',
                        size: 'small',
                        onChange: (e) => {
                            localDispatch({
                                utcMinsOffset: utcMinsOffset ?? 0,
                                localHr: parseInt(e.target.value),
                            });
                        } }), _jsx(TextField, { value: localMin,
                        label: 'Minute',
                        type: 'number',
                        size: 'small',
                        onChange: (e) => {
                            localDispatch({
                                utcMinsOffset: utcMinsOffset ?? 0,
                                localMin: parseInt(e.target.value),
                            });
                        } })] }), _jsx(FormLabel, { children: "Duration" }), _jsxs(Stack, { direction: 'row', gap: 1, children: [_jsx(TextField, { value: durationMins,
                        label: 'Slot duration (minutes)',
                        type: 'number',
                        size: 'small',
                        onChange: (e) => {
                            localDispatch({ durationMins: parseInt(e.target.value) });
                        } }), _jsx(TextField, { value: totalDurationHours,
                        label: 'Total duration (hours)',
                        type: 'number',
                        size: 'small',
                        onChange: (e) => {
                            localDispatch({
                                totalDurationHours: parseInt(e.target.value),
                            });
                        } })] }), _jsx(Button, { variant: 'contained',
                onClick: () => handleCreateSlots(), children: "Create Slots" })] }));
};
